import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toast } from 'react-toastify';
import { pluginCode } from 'drapcode-constant';
import * as actions from './redux/actions';
import * as commonActions from '../common/redux/actions';
import CustomSelect from '../home/collections/CustomSelect';
import { showAlertHtml } from '../../common/utils/alert';
import CollectionHeaderNav from './CollectionHeaderNav';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import ParentCard from '../common/ParentCard';
import CustomLoader from '../common/CustomLoader';
import types from '../../common/formElements/CollectionSectionType';
import { isProjectReadOnly } from '../../common/utils/api_utils';

const googleCalendarFields = [
  { value: 'kind', label: 'Kind' },
  { value: 'etag', label: 'ETag' },
  { value: 'id', label: 'Event ID' },
  { value: 'status', label: 'Status' },
  { value: 'htmlLink', label: 'Event Link' },
  { value: 'created', label: 'Created Time' },
  { value: 'updated', label: 'Updated Time' },
  { value: 'summary', label: 'Event Title (summary)' },
  { value: 'description', label: 'Description' },
  { value: 'creator.email', label: 'Creator Email' },
  { value: 'creator.displayName', label: 'Creator Name' },
  { value: 'organizer.email', label: 'Organizer Email' },
  { value: 'organizer.displayName', label: 'Organizer Name' },
  { value: 'start.dateTime', label: 'Start Time' },
  { value: 'end.dateTime', label: 'End Time' },
  { value: 'iCalUID', label: 'iCal UID' },
  { value: 'sequence', label: 'Sequence' },
  { value: 'attendees', label: 'Attendees (array)' },
  { value: 'hangoutLink', label: 'Video Conference Link' },
  { value: 'conferenceData.entryPoints', label: 'Conference Entry Points' },
  { value: 'conferenceData.conferenceId', label: 'Conference ID' },
  { value: 'reminders.useDefault', label: 'Use Default Reminders' },
  { value: 'reminders.overrides', label: 'Reminder Overrides' },
  { value: 'eventType', label: 'Event Type' },
];

export class GoogleCalendarMapping extends React.Component {
  static propTypes = {
    collections: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    commonActions: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const { match, actions } = this.props;
    const { params } = match;
    const { projectId, collectionId, version } = params;
    actions.loadProjectCollections({ projectId, version });
    actions.loadProjectCollectionDetail({ projectId, collectionId, version });
    actions.loadProjectPlugins({ projectId, version });
  }

  handleSubmit = (values) => {
    const { collections, match, actions, commonActions } = this.props;
    const { selectedCollection } = collections;
    const { projectId, version } = match.params;
    const mapping = Object.entries(values)
      .filter(([_, v]) => v)
      .map(([fieldName, googleField]) => ({
        collectionField: fieldName,
        googleCalendarResponse: googleField.value,
      }));
    if (mapping.length === 0) {
      toast.error('Please map at least one field.');
      return;
    }
    const data = { googleCalendarMapping: mapping };
    const preConfirm = () => {
      actions.createGoogleCalendarMappingInCollection({
        projectId,
        version,
        data,
        selectedCollection,
      });
      commonActions.trackCollectionChange({
        uuid: selectedCollection.uuid,
        name: selectedCollection.collectionName,
      });
    };
    showAlertHtml(
      selectedCollection.googleCalendarMapping.length
        ? 'Update Google Calendar mapping?'
        : 'Save Google Calendar mapping?',
      preConfirm,
      selectedCollection.googleCalendarMapping.length ? 'Yes, update' : 'Yes, save',
      'info',
    );
  };

  getInitialValues = (selectedCollection) => {
    const mapping = selectedCollection.googleCalendarMapping || [];
    return selectedCollection.fields.reduce((acc, field) => {
      const existing = mapping.find((m) => m.collectionField === field.fieldName);
      acc[field.fieldName] = existing
        ? googleCalendarFields.find((f) => f.value === existing.googleCalendarResponse)
        : '';
      return acc;
    }, {});
  };

  render() {
    const { collections, match } = this.props;
    const {
      selectedCollection,
      loadProjectCollectionsPending,
      loadProjectCollectionDetailPending,
      plugins,
    } = collections;
    const { projectId, version } = match.params;
    const hasGoogleCalendarPlugin = plugins.some(
      (plugin) => plugin.code === pluginCode.GOOGLE_CALENDAR,
    );
    const isReadOnly = isProjectReadOnly(projectId);
    if (loadProjectCollectionsPending || loadProjectCollectionDetailPending) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.GOOGLE_CALENDAR_MAPPING}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
          />
          <div className="text-center">
            <CustomLoader loading size="20px" color="#303453" />
          </div>
        </CommonPageComponent>
      );
    }

    if (!hasGoogleCalendarPlugin) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.GOOGLE_CALENDAR_MAPPING}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
            loadProjectCollectionsPending={loadProjectCollectionsPending}
            loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
          />
          <ParentCard
            icon="googleCalendarMapping"
            header={`Google Calendar Mapping ${
              selectedCollection && ` For ${selectedCollection.collectionTitle}`
            }`}
            subtitle="Create Google Calendar Mapping for collection."
          >
            <div className="text-center">
              <h5>Please install the Google Calendar plugin to manage mapping.</h5>
            </div>
          </ParentCard>
        </CommonPageComponent>
      );
    }

    const initialValues = this.getInitialValues(selectedCollection);

    return (
      <CommonPageComponent>
        <CollectionHeaderNav
          label={types.GOOGLE_CALENDAR_MAPPING}
          projectId={projectId}
          version={version}
          selectedCollection={selectedCollection}
        />
        <ParentCard
          icon="googleCalendarMapping"
          header={`Google Calendar Mapping for ${selectedCollection.collectionTitle}`}
          subtitle="Map your collection fields to Google Calendar event fields"
        >
          <Formik initialValues={initialValues} onSubmit={isReadOnly ? null : this.handleSubmit}>
            {({ values, setFieldValue, setFieldTouched }) => (
              <Form className="form-horizontal">
                <div className="row font-weight-bold mb-3">
                  <div className="col">
                    <h5>Collection Field ({selectedCollection.fields.length})</h5>
                  </div>
                  <div className="col">
                    <h5>Google Calendar Response Field</h5>
                  </div>
                </div>
                {selectedCollection.fields.map((field) => (
                  <div className="row mx-auto mb-3" key={field.fieldName}>
                    <div className="col-md col-lg col-sm-12 col-xs-12">
                      <span className="input-group-text">{field.fieldTitle.en}</span>
                    </div>
                    <div className="col-md col-lg col-sm-12 col-xs-12">
                      <CustomSelect
                        name={field.fieldName}
                        isMulti={false}
                        value={values[field.fieldName]}
                        options={googleCalendarFields}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        placeholder="Select Google Calendar Response field"
                      />
                    </div>
                  </div>
                ))}
                <div className="text-right mt-3">
                  <button
                    type="submit"
                    className="btn btn-theme-submit btn-md"
                    disabled={isReadOnly}
                    title={isReadOnly && 'Read-Only Mode'}
                  >
                    {selectedCollection.googleCalendarMapping &&
                    selectedCollection.googleCalendarMapping.length ? (
                      <>
                        <i className="wb webconnect-save-regular"></i>&nbsp;Update&nbsp;
                      </>
                    ) : (
                      <>
                        <i className="wb webconnect-plus"></i>&nbsp;Save&nbsp;
                      </>
                    )}
                    <CustomLoader loading={false} />
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </ParentCard>
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    collections: state.collections,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
    commonActions: bindActionCreators({ ...commonActions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(GoogleCalendarMapping);
